import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Zap, Thermometer, Package, Battery } from 'lucide-react';

const iconMap = {
  zap: Zap,
  thermometer: Thermometer,
  package: Package,
  battery: Battery
};

const OperationSection = ({ data }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section 
      ref={ref} 
      className="relative py-20 lg:py-32 overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: 'url(/assets/operation-bg.png)',
        }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/90 via-[#0A0A0A]/85 to-[#0A0A0A]/95" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="text-[#C8102E] text-base md:text-lg tracking-[0.2em] font-medium uppercase mb-6">
            {data.label}
          </div>
          
          <motion.h2
            className="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 whitespace-pre-line"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {data.headline}
          </motion.h2>

          <motion.p
            className="text-[#F0EBE3] text-lg leading-relaxed max-w-2xl mx-auto mb-16"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {data.subtext}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {data.features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            
            return (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ 
                  duration: 0.6, 
                  delay: 0.5 + index * 0.15,
                  ease: [0.25, 0.4, 0.25, 1]
                }}
                whileHover={{ 
                  y: -10, 
                  scale: 1.05,
                  transition: { duration: 0.3 }
                }}
                className="bg-[#1A1A1A] rounded-lg p-6 text-center border border-[#2A2A2A] hover:border-[#C8102E]/50 transition-all duration-300 cursor-pointer"
              >
                <motion.div 
                  className="flex justify-center mb-4"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={isInView ? { scale: 1, rotate: 0 } : {}}
                  transition={{ 
                    type: "spring",
                    stiffness: 200,
                    delay: 0.6 + index * 0.15
                  }}
                >
                  <div className="w-12 h-12 rounded-full bg-[#C8102E]/10 flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-[#C8102E]" />
                  </div>
                </motion.div>

                <motion.h3
                  className="text-white text-lg font-bold mb-2"
                  style={{ fontFamily: "'Ubuntu', sans-serif" }}
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.8 + index * 0.15 }}
                >
                  {feature.title}
                </motion.h3>

                <motion.p
                  className="text-[#F0EBE3]/70 text-sm leading-relaxed"
                  style={{ fontFamily: "'Ubuntu', sans-serif" }}
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.9 + index * 0.15 }}
                >
                  {feature.description}
                </motion.p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="text-center"
        >
          <p
            className="text-[#F0EBE3]/80 text-lg italic"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
          >
            {data.connector}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default OperationSection;
